<template>
  <validation-observer ref="feeDiscountForm" v-slot="{ invalid }">
    <lenon-modal :title="`${updateFeeDiscountMode ? 'Update' : 'Create'} Fee Discount`" :show="feeDiscountModalOpened"
      :show-overlay="deletingFeeDiscount" size="md" @onClose="closeFeeDiscountModal()">
      <error-display :error="error" />

      <b-row class="align-items-start">
        <div class="col-7">
          <lenon-select v-model="selectedFeeDiscountId" :options="feeDiscounts" label-name="title" value-name="id"
            placeholder="Fee Discounts" @input="populateFeeDiscountForm" />
        </div>
        <div class="col-2">
          <lenon-dropdown icon="TrashIcon">
            <b-dropdown-item @click="deleteDiscount()">
              Yes
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item>
              No
            </b-dropdown-item>
          </lenon-dropdown>
        </div>
        <div class="col-2">
          <lenon-button variant="flat-danger" icon="XIcon" label="" tool-tip-text="Clear Form"
            @onClick="resetFeeDiscountForm()" />
        </div>
      </b-row>

      <b-row v-if="selectedFeeSetting && selectedFeeSetting.has_been_applied">
        <b-col md="12">
          <p class="text-danger font-weight-bold">
            <strong>Note:</strong> <br>
            This fee discount has been applied to one or more students, updating the discount will affect these students, consider creating a new discount instead. Proceed if your intentions are to update applied discount amount.
          </p>
        </b-col>
      </b-row>

      <lenon-select v-model="feeDiscount.fee_setting_id" name="fee_setting_id" :options="feeSettings"
        label-name="display_name" value-name="id" label="Fee Setting" placeholder="Select a fee setting"
        rules="required" />

      <lenon-input v-model="feeDiscount.title" name="title" placeholder="Enter discount title, e.g. 'More than 3 Wards'"
        rules="required" label="Discount Title" />

      <lenon-select v-model="feeDiscount.discount_type" name="discount_type" :options="discountTypes" label-name="label"
        value-name="value" label="Discount Type" placeholder="Select discount type" rules="required"
       />

      <lenon-input v-model="feeDiscount.discount" name="discount" :placeholder="discountPlaceholder"
        :rules="discountRules" :label="discountLabel" type="number" />

      <div v-if="selectedFeeSetting" class="mt-3">
        <b-card>
          <b-card-header>
            <h6 class="mb-0">Preview</h6>
          </b-card-header>
          <b-card-body>
            <b-row>
              <b-col md="6">
                <strong>Fee Setting:</strong> {{ selectedFeeSetting.title }}
              </b-col>
              <b-col md="6">
                <strong>Original Amount:</strong> {{ $store.getters['auth/currency'] }} {{ selectedFeeSetting.amount }}
              </b-col>
              <b-col md="6">
                <strong>Discount:</strong>
                {{ feeDiscount.discount_type === 'percentage' ? (feeDiscount.discount || 0) + '%' :
                  $store.getters['auth/currency'] + ' ' + (feeDiscount.discount || 0) }}
              </b-col>
              <b-col md="6">
                <strong>Final Amount:</strong>
                <span class="text-success font-weight-bold">
                  {{ $store.getters['auth/currency'] }} {{ calculatedAmount }}
                </span>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </div>

      <template #modal-actions>
        <b-row class="float-right">
          <lenon-button variant="flat-danger" icon="XIcon" class="mr-1" label="Cancel"
            @onClick="closeFeeDiscountModal()" />
          <lenon-button :label="updateFeeDiscountMode ? 'Update' : 'Create'" :disabled="invalid"
            :loading="feeDiscountLoading" loading-text="Loading..."
            @onClick="updateFeeDiscountMode ? updateFeeDiscount() : createFeeDiscount()" />
        </b-row>
      </template>
    </lenon-modal>
  </validation-observer>
</template>

<script>
import LenonModal from '@/lenon/components/LenonModal.vue'
import LenonButton from '@/lenon/components/LenonButton.vue'
import LenonInput from '@/lenon/components/LenonInput.vue'
import LenonSelect from '@/lenon/components/LenonSelect.vue'
import LenonDropdown from '@/lenon/components/LenonDropdown.vue'
import ErrorDisplay from '@/lenon/components/ErrorDisplay.vue'
import showToast from '@/lenon/mixins/showToast'
import confirm from '@/lenon/mixins/confirm'
import {
  BRow, BCol, BCard, BCardHeader, BCardBody, BDropdownItem, BDropdownDivider,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import logData from '@/libs/log'

export default {
  name: 'FeeDiscountSetup',
  components: {
    LenonModal,
    LenonButton,
    LenonInput,
    LenonSelect,
    LenonDropdown,
    ErrorDisplay,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BDropdownItem,
    BDropdownDivider,
    ValidationObserver,
  },
  mixins: [showToast, confirm],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      deletingFeeDiscount: false,
      feeDiscountModalOpened: false,
      updateFeeDiscountMode: false,
      feeDiscountLoading: false,
      selectedFeeDiscountId: null,
      error: {},
      feeDiscount: {
        id: null,
        title: '',
        fee_setting_id: null,
        discount_type: null,
        discount: null,
      },
      discountTypes: [
        { label: 'Fixed Amount', value: 'flat' },
        { label: 'Percentage', value: 'percentage' },
      ],
    }
  },
  computed: {
    feeSettings() {
      return this.$store.getters['feeSettings/feeSettings'].map(fs => ({
        ...fs,
        display_name: `${fs.title} - ${this.$store.getters['auth/currency']} ${fs.amount} (${fs.term} - ${fs.academic_year})`
      }))
    },
    feeDiscounts() {
      return this.$store.getters['feeDiscounts/feeDiscounts']
    },
    selectedFeeSetting() {
      if (!this.feeDiscount.fee_setting_id) return null
      return this.feeSettings.find(fs => fs.id === this.feeDiscount.fee_setting_id)
    },
    discountPlaceholder() {
      if (this.feeDiscount.discount_type === 'percentage') {
        return 'Enter percentage (e.g., 20 for 20%)'
      }
      return `Enter amount in ${this.$store.getters['auth/currency']}`
    },
    discountLabel() {
      if (this.feeDiscount.discount_type === 'percentage') {
        return 'Discount Percentage (%)'
      }
      return `Discount Amount (${this.$store.getters['auth/currency']})`
    },
    discountRules() {
      let rules = 'required|numeric|min_value:0'
      if (this.feeDiscount.discount_type === 'percentage') {
        rules += '|max_value:100'
      }
      return rules
    },
    calculatedAmount() {
      if (!this.selectedFeeSetting || !this.feeDiscount.discount || !this.feeDiscount.discount_type) {
        return this.selectedFeeSetting ? this.selectedFeeSetting.amount : 0
      }

      let amount = this.selectedFeeSetting.amount
      if (this.feeDiscount.discount_type === 'flat') {
        amount = amount - parseFloat(this.feeDiscount.discount)
      } else if (this.feeDiscount.discount_type === 'percentage') {
        amount = amount - (parseFloat(this.feeDiscount.discount) / 100 * amount)
      }
      return Math.max(0, amount).toFixed(2)
    },
  },
  watch: {
    modalOpened(opened) {
      this.feeDiscountModalOpened = opened
    },
    selectedFeeDiscountId(id) {
      if (!id) {
        this.resetFeeDiscountForm()
      }
    },
  },
  methods: {
    resetDiscountValue() {
      this.feeDiscount.discount = null
    },
    resetFeeDiscountForm() {
      this.selectedFeeDiscountId = null
      this.updateFeeDiscountMode = false
      this.feeDiscount = {
        id: null,
        title: '',
        fee_setting_id: null,
        discount_type: null,
        discount: null,
      }
      this.error = {}
      this.$refs.feeDiscountForm.reset()
    },
    populateFeeDiscountForm() {
      if (this.selectedFeeDiscountId) {
        this.updateFeeDiscountMode = true
        const discount = this.feeDiscounts.find(d => d.id === this.selectedFeeDiscountId)
        if (discount) {
          this.feeDiscount = { ...discount }
        }
      } else {
        this.resetFeeDiscountForm()
      }
    },
    closeFeeDiscountModal() {
      this.feeDiscountModalOpened = false
      this.$emit('modalClosed')
    },
    deleteDiscount() {
      if (!this.selectedFeeDiscountId) {
        this.showInfo('Please select a discount to delete')
        return
      }

      this.deletingFeeDiscount = true
      this.$http.delete(`fee-discounts/${this.selectedFeeDiscountId}`)
        .then(() => {
          this.deletingFeeDiscount = false
          this.showSuccess('Fee discount deleted successfully')
          this.$store.commit('feeDiscounts/removeFeeDiscount', this.selectedFeeDiscountId)
          this.selectedFeeDiscountId = null
          this.resetFeeDiscountForm()
        })
        .catch(err => {
          logData(err)
          this.showError('Failed to delete fee discount')
          this.deletingFeeDiscount = false
        })
    },
    createFeeDiscount() {
      this.error = {}
      this.feeDiscountLoading = true

      this.$http.post('fee-discounts', this.feeDiscount)
        .then(res => {
          this.showSuccess('Fee discount created successfully')
          this.feeDiscountLoading = false
          this.$store.commit('feeDiscounts/addFeeDiscount', res.data)
          this.resetFeeDiscountForm()
        })
        .catch(err => {
          this.error = err.response?.data || { message: 'Failed to create discount' }
          logData(err)
          this.showError('Failed to create fee discount')
          this.feeDiscountLoading = false
        })
    },
    updateFeeDiscount() {
      this.error = {}
      if (!this.feeDiscount.id) {
        return
      }

      this.feeDiscountLoading = true
      this.$http.put(`fee-discounts/${this.feeDiscount.id}`, this.feeDiscount)
        .then(res => {
          this.feeDiscountLoading = false
          this.showSuccess('Fee discount updated successfully')
          this.$store.commit('feeDiscounts/updateFeeDiscount', res.data)
        })
        .catch(err => {
          this.error = err.response?.data || { message: 'Failed to update discount' }
          logData(err)
          this.showError('Failed to update fee discount')
          this.feeDiscountLoading = false
        })
    },
  },
}
</script>
